import React, { useState, useCallback, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box, Button, Typography, Divider, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import GameweekDraggableMatchCard from "./GameweekDraggableMatchCard";
import GameweekDroppableMatchZone from "./GameweekDroppableMatchZone";

const GameweekAssignmentPanel = ({
  availableMatches,
  assignedMatches,
  onAssignMatches,
  onRemoveMatches,
  isCompact = false,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeMatch = useMemo(() => {
    if (!activeId) return null;
    return [...availableMatches, ...assignedMatches].find(
      (m) => m.id === activeId
    );
  }, [activeId, availableMatches, assignedMatches]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over) return;

      const matchId = active.id;
      const sourceZone = assignedMatches.find((m) => m.id === matchId)
        ? "assigned"
        : "available";
      const targetZone = over.id;

      if (sourceZone === targetZone) return;

      if (targetZone === "assigned-zone" && sourceZone !== "assigned") {
        onAssignMatches([matchId]);
      } else if (targetZone === "available-zone" && sourceZone === "assigned") {
        onRemoveMatches([matchId]);
      }
    },
    [assignedMatches, onAssignMatches, onRemoveMatches]
  );

  const handleSelectAvailable = (matchId) => {
    setSelectedAvailable((prev) =>
      prev.includes(matchId)
        ? prev.filter((id) => id !== matchId)
        : [...prev, matchId]
    );
  };

  const handleSelectAssigned = (matchId) => {
    setSelectedAssigned((prev) =>
      prev.includes(matchId)
        ? prev.filter((id) => id !== matchId)
        : [...prev, matchId]
    );
  };

  const handleAssignSelected = () => {
    onAssignMatches(selectedAvailable);
    setSelectedAvailable([]);
  };

  const handleRemoveSelected = () => {
    onRemoveMatches(selectedAssigned);
    setSelectedAssigned([]);
  };

  const handleSelectAllAvailable = () => {
    if (selectedAvailable.length === availableMatches.length) {
      setSelectedAvailable([]);
    } else {
      setSelectedAvailable(availableMatches.map((m) => m.id));
    }
  };

  const handleSelectAllAssigned = () => {
    if (selectedAssigned.length === assignedMatches.length) {
      setSelectedAssigned([]);
    } else {
      setSelectedAssigned(assignedMatches.map((m) => m.id));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Available Matches Panel */}
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Available Matches</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                onClick={handleSelectAllAvailable}
                disabled={availableMatches.length === 0}
              >
                {selectedAvailable.length === availableMatches.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
              <Chip
                label={`${availableMatches.length}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>

          <GameweekDroppableMatchZone
            id="available-zone"
            title="Unassigned Matches"
            count={availableMatches.length}
            showCount={false}
            emptyMessage="No unassigned matches"
          >
            <SortableContext
              items={availableMatches.map((m) => m.id)}
              strategy={verticalListSortingStrategy}
            >
              {availableMatches.map((match) => (
                <GameweekDraggableMatchCard
                  key={match.id}
                  match={match}
                  isSelected={selectedAvailable.includes(match.id)}
                  onSelect={handleSelectAvailable}
                  isCompact={isCompact}
                />
              ))}
            </SortableContext>
          </GameweekDroppableMatchZone>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAssignSelected}
              disabled={selectedAvailable.length === 0}
              fullWidth
            >
              Assign{" "}
              {selectedAvailable.length > 0 && `(${selectedAvailable.length})`}
            </Button>
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", md: "block" } }}
        />
        <Divider sx={{ display: { xs: "block", md: "none" } }} />

        {/* Assigned Matches Panel */}
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Assigned to Gameweek</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                onClick={handleSelectAllAssigned}
                disabled={assignedMatches.length === 0}
              >
                {selectedAssigned.length === assignedMatches.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
              <Chip
                label={`${assignedMatches.length}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>

          <GameweekDroppableMatchZone
            id="assigned-zone"
            title="Gameweek Matches"
            count={assignedMatches.length}
            showCount={false}
            emptyMessage="No matches assigned. Drag matches here"
          >
            <SortableContext
              items={assignedMatches.map((m) => m.id)}
              strategy={verticalListSortingStrategy}
            >
              {assignedMatches.map((match) => (
                <GameweekDraggableMatchCard
                  key={match.id}
                  match={match}
                  isSelected={selectedAssigned.includes(match.id)}
                  onSelect={handleSelectAssigned}
                  isCompact={isCompact}
                />
              ))}
            </SortableContext>
          </GameweekDroppableMatchZone>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Button
              variant="outlined"
              startIcon={<RemoveIcon />}
              onClick={handleRemoveSelected}
              disabled={selectedAssigned.length === 0}
              fullWidth
            >
              Remove{" "}
              {selectedAssigned.length > 0 && `(${selectedAssigned.length})`}
            </Button>
          </Box>
        </Box>
      </Box>

      <DragOverlay>
        {activeMatch ? (
          <GameweekDraggableMatchCard
            match={activeMatch}
            isCompact={isCompact}
            showCheckbox={false}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default GameweekAssignmentPanel;
